// ─── DOM Generator — converts element tree to clean HTML/CSS/JS ───────────────
import { BuilderElement, BuilderPage, BuilderProject, CSSClass, resolveStyles, ViewportMode, VIEWPORT_WIDTHS } from './builderTypes';

function camelToKebab(str: string): string {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase();
}

function stylesToCss(styles: Record<string, string | undefined>): string {
  return Object.entries(styles)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${camelToKebab(k)}:${v}`)
    .join(';');
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderElementToHtml(
  id: string,
  elements: Record<string, BuilderElement>,
  cssClasses: Record<string, CSSClass>,
  viewport: ViewportMode = 'desktop',
  depth = 0
): string {
  const el = elements[id];
  if (!el || el.hidden) return '';

  const indent = '  '.repeat(depth);
  const childIndent = '  '.repeat(depth + 1);

  const resolved = resolveStyles(el, viewport, cssClasses);
  const styleStr = stylesToCss(resolved);
  const classNames = el.cssClasses?.map(cid => cssClasses[cid]?.name).filter(Boolean).join(' ') || '';
  const classAttr = classNames ? ` class="${classNames}"` : '';
  const styleAttr = styleStr ? ` style="${styleStr}"` : '';

  const children = el.children.map(cid => renderElementToHtml(cid, elements, cssClasses, viewport, depth + 1)).join('\n');
  const childContent = children ? `\n${children}\n${indent}` : '';

  // Build data attributes for interactions
  const interactionAttrs = el.interactions?.length
    ? ` data-interactions="${escapeHtml(JSON.stringify(el.interactions))}"`
    : '';

  switch (el.type) {
    case 'section':
      return `${indent}<section${classAttr}${styleAttr}${interactionAttrs}>${childContent}</section>`;
    case 'container': case'div': case'flex-container': case'grid-container':
      return `${indent}<div${classAttr}${styleAttr}${interactionAttrs}>${childContent}</div>`;
    case 'heading': {
      const tag = resolved.fontSize && parseInt(resolved.fontSize) >= 36 ? 'h1' : parseInt(resolved.fontSize || '0') >= 28 ? 'h2' : parseInt(resolved.fontSize || '0') >= 22 ? 'h3' : 'h2';
      return `${indent}<${tag}${classAttr}${styleAttr}>${escapeHtml(el.content)}</${tag}>`;
    }
    case 'paragraph':
      return `${indent}<p${classAttr}${styleAttr}>${escapeHtml(el.content)}</p>`;
    case 'text':
      return `${indent}<span${classAttr}${styleAttr}>${escapeHtml(el.content)}</span>`;
    case 'button':
      return `${indent}<button${classAttr}${styleAttr} type="button">${escapeHtml(el.content)}</button>`;
    case 'image': {
      const src = el.attributes.src || '';
      const alt = el.attributes.alt || '';
      return `${indent}<img${classAttr}${styleAttr} src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" loading="lazy" />`;
    }
    case 'link': {
      const href = el.attributes.href || '#';
      const target = el.attributes.target || '_self';
      return `${indent}<a${classAttr}${styleAttr} href="${escapeHtml(href)}" target="${escapeHtml(target)}">${escapeHtml(el.content)}</a>`;
    }
    case 'spacer':
      return `${indent}<div${classAttr}${styleAttr} aria-hidden="true"></div>`;
    case 'divider':
      return `${indent}<hr${classAttr}${styleAttr} />`;
    default:
      return `${indent}<div${classAttr}${styleAttr}>${childContent}</div>`;
  }
}

function generateCssClasses(cssClasses: Record<string, CSSClass>): string {
  const lines: string[] = [];
  for (const cls of Object.values(cssClasses)) {
    const base = stylesToCss(cls.styles);
    if (base) lines.push(`.${cls.name} { ${base} }`);
    // Breakpoint overrides
    if (cls.breakpointStyles?.tablet) {
      lines.push(`@media (max-width: ${VIEWPORT_WIDTHS.tablet}px) { .${cls.name} { ${stylesToCss(cls.breakpointStyles.tablet)} } }`);
    }
    if (cls.breakpointStyles?.['mobile-landscape']) {
      lines.push(`@media (max-width: ${VIEWPORT_WIDTHS['mobile-landscape']}px) { .${cls.name} { ${stylesToCss(cls.breakpointStyles['mobile-landscape'])} } }`);
    }
    if (cls.breakpointStyles?.['mobile-portrait']) {
      lines.push(`@media (max-width: ${VIEWPORT_WIDTHS['mobile-portrait']}px) { .${cls.name} { ${stylesToCss(cls.breakpointStyles['mobile-portrait'])} } }`);
    }
  }
  return lines.join('\n');
}

function generateResponsiveCss(elements: Record<string, BuilderElement>): string {
  const tabletRules: string[] = [];
  const mobileLRules: string[] = [];
  const mobilePRules: string[] = [];

  for (const el of Object.values(elements)) {
    if (el.hidden) continue;
    const id = `[data-el-id="${el.id}"]`;
    if (el.breakpointStyles?.tablet) {
      tabletRules.push(`${id} { ${stylesToCss(el.breakpointStyles.tablet)} }`);
    }
    if (el.breakpointStyles?.['mobile-landscape']) {
      mobileLRules.push(`${id} { ${stylesToCss(el.breakpointStyles['mobile-landscape'])} }`);
    }
    if (el.breakpointStyles?.['mobile-portrait']) {
      mobilePRules.push(`${id} { ${stylesToCss(el.breakpointStyles['mobile-portrait'])} }`);
    }
    // Visibility
    if (el.visibility) {
      if (el.visibility.tablet === false) tabletRules.push(`${id} { display: none !important; }`);
      if (el.visibility.mobileLandscape === false) mobileLRules.push(`${id} { display: none !important; }`);
      if (el.visibility.mobilePortrait === false) mobilePRules.push(`${id} { display: none !important; }`);
    }
  }

  const parts: string[] = [];
  if (tabletRules.length) parts.push(`@media (max-width: ${VIEWPORT_WIDTHS.tablet}px) {\n${tabletRules.join('\n')}\n}`);
  if (mobileLRules.length) parts.push(`@media (max-width: ${VIEWPORT_WIDTHS['mobile-landscape']}px) {\n${mobileLRules.join('\n')}\n}`);
  if (mobilePRules.length) parts.push(`@media (max-width: ${VIEWPORT_WIDTHS['mobile-portrait']}px) {\n${mobilePRules.join('\n')}\n}`);
  return parts.join('\n\n');
}

function generateInteractionJs(): string {
  return `
(function() {
  // Interaction engine
  function applyInteraction(el, interaction) {
    var style = el.style;
    var duration = (interaction.duration || 400) + 'ms';
    var delay = (interaction.delay || 0) + 'ms';
    var easing = interaction.easing || 'ease';
    style.transition = 'all ' + duration + ' ' + easing + ' ' + delay;
    switch (interaction.animation) {
      case 'fade': style.opacity = interaction.direction === 'out' ? '0' : '1'; break;
      case 'scale': style.transform = 'scale(' + (interaction.direction === 'out' ? '0.8' : '1') + ')'; break;
      case 'slide':
        var dir = interaction.direction || 'up';
        var tx = dir === 'left' ? '-40px' : dir === 'right' ? '40px' : '0';
        var ty = dir === 'up' ? '-40px' : dir === 'down' ? '40px' : '0';
        style.transform = 'translate(' + tx + ',' + ty + ')'; break;
      case 'rotate': style.transform = 'rotate(' + (interaction.value || '360deg') + ')'; break;
      case 'opacity': style.opacity = interaction.value || '0.5'; break;
      case 'color': style.color = interaction.value || '#000'; break;
    }
  }

  document.querySelectorAll('[data-interactions]').forEach(function(el) {
    try {
      var interactions = JSON.parse(el.getAttribute('data-interactions') || '[]');
      interactions.forEach(function(ix) {
        if (!ix.enabled) return;
        if (ix.trigger === 'pageLoad') {
          setTimeout(function() { applyInteraction(el, ix); }, ix.delay || 0);
        } else if (ix.trigger === 'hover') {
          el.addEventListener('mouseenter', function() { applyInteraction(el, ix); });
        } else if (ix.trigger === 'click') {
          el.addEventListener('click', function() { applyInteraction(el, ix); });
        } else if (ix.trigger === 'scrollIntoView') {
          var observer = new IntersectionObserver(function(entries) {
            entries.forEach(function(entry) {
              if (entry.isIntersecting) { applyInteraction(el, ix); observer.disconnect(); }
            });
          }, { threshold: 0.1 });
          observer.observe(el);
        }
      });
    } catch(e) {}
  });
})();
`.trim();
}

export interface GeneratedPage {
  pageId: string;
  pageName: string;
  slug: string;
  html: string;
}

export function generatePageHtml(page: BuilderPage, project: BuilderProject): string {
  const cssClasses = page.cssClasses || {};
  const seo = page.seo || { slug: '/', title: page.name, metaDescription: '', ogImage: '' };
  const siteSeo = project.siteSeo;
  const siteUrl = siteSeo?.siteUrl || '';
  const canonicalUrl = seo.canonicalUrl || (siteUrl ? `${siteUrl.replace(/\/$/, '')}${seo.slug}` : '');
  const ogTitle = seo.ogTitle || seo.title || page.name;
  const ogDesc = seo.ogDescription || seo.metaDescription || '';
  const ogImage = seo.ogImage || siteSeo?.ogImage || '';
  const robots = seo.robots || siteSeo?.robots || 'index,follow';
  const keywords = seo.keywords || '';

  const bodyHtml = page.rootChildren
    .map(id => renderElementToHtml(id, page.elements, cssClasses))
    .join('\n');

  const classCss = generateCssClasses(cssClasses);
  const responsiveCss = generateResponsiveCss(page.elements);
  const interactionJs = generateInteractionJs();

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>${escapeHtml(seo.title || page.name)}</title>
  ${seo.metaDescription ? `<meta name="description" content="${escapeHtml(seo.metaDescription)}" />` : ''}
  ${keywords ? `<meta name="keywords" content="${escapeHtml(keywords)}" />` : ''}
  <meta name="robots" content="${escapeHtml(robots)}" />
  ${canonicalUrl ? `<link rel="canonical" href="${escapeHtml(canonicalUrl)}" />` : ''}
  <!-- Open Graph -->
  <meta property="og:type" content="website" />
  <meta property="og:title" content="${escapeHtml(ogTitle)}" />
  ${ogDesc ? `<meta property="og:description" content="${escapeHtml(ogDesc)}" />` : ''}
  ${ogImage ? `<meta property="og:image" content="${escapeHtml(ogImage)}" />` : ''}
  ${canonicalUrl ? `<meta property="og:url" content="${escapeHtml(canonicalUrl)}" />` : ''}
  <!-- Twitter Card -->
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="${escapeHtml(ogTitle)}" />
  ${ogDesc ? `<meta name="twitter:description" content="${escapeHtml(ogDesc)}" />` : ''}
  ${ogImage ? `<meta name="twitter:image" content="${escapeHtml(ogImage)}" />` : ''}
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; font-family: sans-serif; }
    img { max-width: 100%; height: auto; }
    ${classCss}
    ${responsiveCss}
  </style>
</head>
<body>
${bodyHtml}
<script>
${interactionJs}
</script>
</body>
</html>`;
}

export function generateSitemapXml(project: BuilderProject): string {
  const siteUrl = project.siteSeo?.siteUrl || 'https://example.com';
  const base = siteUrl.replace(/\/$/, '');
  const now = new Date().toISOString().split('T')[0];
  const urls = project.pages.map(page => {
    const slug = page.seo?.slug || '/';
    const loc = slug === '/' ? base + '/' : `${base}${slug.startsWith('/') ? slug : '/' + slug}`;
    return `  <url>\n    <loc>${loc}</loc>\n    <lastmod>${now}</lastmod>\n    <changefreq>weekly</changefreq>\n    <priority>${page.isHomepage ? '1.0' : '0.8'}</priority>\n  </url>`;
  }).join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>`;
}

export function validateProject(project: BuilderProject): { valid: boolean; errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!project.name.trim()) errors.push('Project name is required');
  if (project.pages.length === 0) errors.push('Project must have at least one page');

  const homepage = project.pages.find(p => p.isHomepage);
  if (!homepage) warnings.push('No homepage set — first page will be used');

  for (const page of project.pages) {
    if (!page.seo?.title) warnings.push(`Page "${page.name}" is missing an SEO title`);
    if (!page.seo?.metaDescription) warnings.push(`Page "${page.name}" is missing a meta description`);
    if (Object.keys(page.elements).length === 0) warnings.push(`Page "${page.name}" has no content`);
  }

  if (!project.siteSeo?.siteUrl) warnings.push('Site URL not set — canonical URLs and sitemap will be incomplete');

  return { valid: errors.length === 0, errors, warnings };
}

export function exportProjectJson(project: BuilderProject): string {
  return JSON.stringify(project, null, 2);
}

export function importProjectJson(json: string): BuilderProject | null {
  try {
    const data = JSON.parse(json);
    if (!data.id || !data.name || !Array.isArray(data.pages)) return null;
    return data as BuilderProject;
  } catch {
    return null;
  }
}
